import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import apiClient from '../../../../requests/api/apiClient';
import { navigateToChart } from '../../../../utils/helpers';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
} from '../../types';

import { SOIL_MOISTURE_NAME, CHART_TITLE_HUMEDAD_SUELO } from '../../../../constants/graphs';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import SoilMoistureConfigContent from './SoilMoistureConfigContent';
import { getChartConfig, getLinearTooltip } from '../utils';
import { CriticalPoint } from './types';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

const plotLinesStyle = {
  color: '#000000',
  width: 1,
  zIndex: 4,
  label: {
    verticalAlign: 'bottom',
    align: 'right',
    y: -2,
    x: -10,
    useHTML: true,
    style: {
      color: '#000000',
      fontSize: '12px',
    },
  },
};

const ploteableLine = ['criticalPoint1', 'criticalPoint2', 'criticalPoint3'];

function SoilMoistureChart({
  chartView,
  sectorId,
  farmId,
  generalLines = undefined,
  refreshCounter,
  hideYValues = false,
  hideLegend = true,
  hideXValues = false,
  height = 450,
  title = CHART_TITLE_HUMEDAD_SUELO,
  yAxisTitle = 'Humedad Suelo Acumulada (%)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  generalLines?: CriticalPoint[] | undefined
  refreshCounter?: number
  hideYValues?: boolean
  hideLegend?: boolean
  hideXValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  maxValue?: number
  minValue?: number
  selectedDepths?: string[]
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  configuredAreas?: any[],
  fullScreen?: boolean;
  onConversationCreated?: () => void;
}) {
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    absoluteCategories: [],
  });
  const [yAxisRange, setYAxisRange] = useState({ min: 20, max: 100 });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  // Bumped when the user saves config from the in-place dialog so the chart
  // re-loads its config and refetches data without requiring a page navigation.
  const [internalConfigRefreshTrigger, setInternalConfigRefreshTrigger] = useState(0);

  const [configChart, setConfigChart] = useState<{
    maxValue: number;
    minValue: number;
    internalSelectedDepths: string[];
    phaseLines: any;
  }>({
    maxValue: 0,
    minValue: 0,
    internalSelectedDepths: [],
    phaseLines: [],
  });

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);
  const [hideDepthsText, setHideDepthsText] = useState(false);
  const [isInitialConfigLoaded, setIsInitialConfigLoaded] = useState(false);
  const [phenologicalStatesLabels, setPhenologicalStatesLabels] = useState<{
    [key: string]: string;
  }>({});

  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const chartName = 'humedad-suelo';

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const [plotLines, setPlotLines] = useState<any>(generalLines?.map((line) => {
    if (ploteableLine.includes(line.label)) {
      return {
        ...plotLinesStyle,
        value: line.value,
        label: {
          text: `${line.customLabel} (${line.value})`,
          ...plotLinesStyle.label,
        },
      };
    }
    return null;
  }).filter(Boolean));

  const areaRangeStyles = {
    type: 'arearange',
    name: 'Suma de Profundidades',
    lineColor: '#303030',
    fillOpacity: 1,
    opacity: 1,
    zIndex: -1,
    enableMouseTracking: false,
    animation: false,
    nullInteraction: true,
    inactiveOtherPoints: true,
  };

  useEffect(() => {
    const loadInitialConfig = async () => {
      if (interval) {
        try {
          const config = await apiClient.soilMoistureConfig.soilMoistureConfigDetail(
            sectorId,
            {
              startDate: interval.startDate,
              endDate: interval.endDate,
            },
          );
          const configMoisture = config.data.soilMoistureConfig;
          const configPhaseLines = (configMoisture?.chartLines || []
          )?.map((line) => ({
            ...areaRangeStyles,
            data: line.data,
            color: line.color,
            name: line.customLabel,
          }));

          setPhenologicalStatesLabels(configMoisture?.phenologicalStatesLabels || {});

          // Batch state updates together
          const newPlotLines = configMoisture?.generalLines?.map((line) => {
            if (ploteableLine.includes(line.label)) {
              return {
                ...plotLinesStyle,
                value: line.value,
                label: {
                  text: `${line.customLabel} (${line.value})`,
                  ...plotLinesStyle.label,
                },
              };
            }
            return null;
          }).filter(Boolean);

          setPlotLines(newPlotLines);
          setConfigChart({
            maxValue: configMoisture?.maxValue || 0,
            minValue: configMoisture?.minValue || 0,
            internalSelectedDepths: configMoisture?.levels || [],
            phaseLines: configPhaseLines,
          });
        } catch (error) {
          console.error('Error loading initial config:', error);
        } finally {
          setIsInitialConfigLoaded(true);
        }
      }
    };

    loadInitialConfig();
  }, [interval, refreshCounter, internalConfigRefreshTrigger]);

  useEffect(() => {
    const newPlotLines = generalLines?.map((line) => {
      if (ploteableLine.includes(line.label)) {
        return {
          ...plotLinesStyle,
          value: line.value,
          label: {
            text: `${line.customLabel} (${line.value})`,
            ...plotLinesStyle.label,
          },
        };
      }
      return null;
    }).filter(Boolean);
    if (newPlotLines) {
      setPlotLines(newPlotLines);
    }
  }, [generalLines]);

  useEffect(() => {
    let isMounted = true;

    const safeFetchData = async () => {
      setIsLoading(true);
      try {
        if (isInitialConfigLoaded && interval) {
          const { data } = await apiClient.readings.getSoilMoistureData({
            farmId,
            sectorId,
            startDate: interval.startDate,
            endDate: interval.endDate,
            sensorType: 'Soil Moisture',
            telemetryType: selectedSector?.telemetryType || 'wiseconn',
            sn: selectedSector?.sn || '',
            chart: SOIL_MOISTURE_NAME,
          });
          const { summedSeries, absoluteCategories } = data;
          const dataMin = data.min;
          const dataMax = data.max;

          if (!isMounted) return;

          const min = Math.min(configChart?.minValue || 0, dataMin) - 1;
          const max = Math.max(configChart?.maxValue || 0, dataMax) + 1;

          setYAxisRange({ min, max });
          setChartData({
            series: configChart.internalSelectedDepths?.length > 0
              ? [
                {
                  type: 'line',
                  name: 'Suma de Profundidades (%)',
                  data: summedSeries,
                  zIndex: 0,
                  color: '#001AB3',
                },
                ...configChart.phaseLines,
              ]
              : [{
                type: 'line',
                name: 'Sin datos seleccionados',
                data: [],
              }],
            absoluteCategories,
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsInitialConfigLoaded(false);
        }
      }
    };

    safeFetchData();

    return () => {
      isMounted = false;
    };
  }, [isInitialConfigLoaded, interval]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(
      this: Highcharts.TooltipFormatterContextObject,
    ): string {
      return getLinearTooltip(this, conversations);
    },
  }), [chartData.absoluteCategories, conversations]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            SOIL_MOISTURE_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;
    const yAxis = {
      startOnTick: false,
      endOnTick: false,
      min: yAxisRange.min,
      max: yAxisRange.max,
      plotLines,
      labels: {
        enabled: !hideYValues,
      },
      title: {
        text: yAxisTitle,
      },
      gridLineWidth: 0,
    };

    const startDateValue = dayjs(interval.startDate).valueOf();
    const endDateValue = dayjs(interval.endDate).valueOf();

    return getChartConfig(
      chartData,
      height,
      hideXValues,
      yAxis as Highcharts.YAxisOptions,
      startDateValue,
      endDateValue,
      phenologicalStatesLabels,
    );
  }, [
    interval,
    yAxisRange.min,
    yAxisRange.max,
    plotLines,
    hideYValues,
    yAxisTitle,
    chartData,
    height,
    hideXValues,
  ]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <div>
        {((chartView === 'dashboard') && !hideDepthsText) && (
          <p style={{ marginLeft: '80px', fontSize: '12px', color: '#666' }}>
            Profundidades seleccionadas: {configChart.internalSelectedDepths.length > 0
            ? configChart.internalSelectedDepths.join(', ') : 'Ninguna'}
          </p>
        )}
        {((chartView === 'fullScreen') && !hideDepthsText) && (
          <p style={{
            marginLeft: '80px', marginTop: '10px', marginBottom: '-15px', fontSize: '16px', color: '#000000',
          }}>
            Profundidades seleccionadas: {configChart.internalSelectedDepths.length > 0
            ? configChart.internalSelectedDepths.join(', ') : 'Ninguna'}
          </p>
        )}
        <BaseHighchart
          sectorId={sectorId}
          graphName={SOIL_MOISTURE_NAME}
          setHideDepthsText={setHideDepthsText}
          chartView={chartView}
          title={title}
          yAxisTitle={yAxisTitle}
          adjustYWithLegend={true}
          series={chartData.series as Highcharts.SeriesOptionsType[]}
          tooltip={customTooltip}
          chartConfig={chartConfig}
          filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
          onConfigClick={handleConfigClick}
          onFullScreenClick={handleFullScreenClick}
          hideYValues={hideYValues}
          legend={{ enabled: !hideLegend }}
          setModalState={setModalState}
          conversations={conversations}
          absoluteCategories={chartData.absoluteCategories}
          height={height}
          showFullScreen={true}
          zoomSync={true}
          interval={interval}
        />
      </div>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={SOIL_MOISTURE_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Humedad de suelo"
      >
        <SoilMoistureConfigContent
          sectorId={sectorId}
          onConfigSaved={() => {
            setInternalConfigRefreshTrigger((prev) => prev + 1);
            setConfigDialogOpen(false);
          }}
        />
      </ChartConfigDialog>
    </>
  );
}

export default SoilMoistureChart;
